import { useEffect, useState, type ReactNode } from "react";
import { ShieldAlert, AlertCircle } from "lucide-react";
import { ApiError } from "@/lib/api";
import { UnsupportedFileModal, unsupportedReasonFromError } from "./unsupported-file-modal";

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 text-[13px] bg-red-500/[0.06] border border-red-500/20 rounded-xl px-4 py-3">
      <ShieldAlert className="size-5 text-red-400 shrink-0 mt-0.5" />
      <div>
        <p className="font-bold text-red-300 mb-0.5">{title}</p>
        <p className="text-red-400/70 text-[12px] leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

/** A generation error: the content cards, a short line, or the unsupported-file popup for a rejected image. */
export function NsfwOrError({ message, error }: { message?: string; error?: unknown }) {
  const code = error instanceof ApiError ? error.code : undefined;
  message ??= error instanceof Error ? error.message : undefined;
  const imageReason = unsupportedReasonFromError(error);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => setDismissed(false), [error]);

  if (imageReason) {
    return <UnsupportedFileModal reason={dismissed ? null : imageReason} onClose={() => setDismissed(true)} />;
  }

  if (code === "MODERATION_BLOCKED") {
    return (
      <Card title="This image or prompt can't be used">
        Our safety checks flagged this request. CouchStack does not allow content
        that appears to involve minors or that breaks our content rules. Please
        try a different image or prompt.
      </Card>
    );
  }

  const isNsfw = code === "NSFW_CONTENT" || message?.includes("acceptable use policy") || message?.includes("NSFW");
  if (isNsfw) {
    return (
      <Card title="Content not allowed">
        Your prompt was flagged by our content filter. CouchStack does not
        allow NSFW, violent, hateful, or otherwise prohibited content. Please
        revise your prompt and try again.
      </Card>
    );
  }

  return (
    <div className="flex items-center gap-2 text-xs text-red-400/80">
      <AlertCircle className="size-3.5 shrink-0" />
      {message || "Something went wrong. Please try again."}
    </div>
  );
}
